// Multiplayer client: connects via socket.io, samples local pose at ~20 Hz,
// emits lap events when LapTimer.lastLap changes, and renders peer karts via
// a 100ms-delayed interpolation buffer (lerp position, slerp rotation).

import * as THREE from 'three';
import { io } from 'socket.io-client';

const SEND_HZ = 20;
const SEND_INTERVAL = 1 / SEND_HZ;
const INTERP_DELAY_MS = 100;
const MAX_BUFFER = 12;
const CONNECT_TIMEOUT_MS = 4000;
const RECONNECT_ATTEMPTS = 2;

const VEHICLE_KEYS = [
	'vehicle-truck-yellow',
	'vehicle-truck-green',
	'vehicle-truck-purple',
	'vehicle-truck-red',
];

function pickModelKey( models, playerModel, fallbackIndex ) {

	if ( playerModel && models[ playerModel ] ) return playerModel;

	for ( let i = 0; i < VEHICLE_KEYS.length; i ++ ) {

		const k = VEHICLE_KEYS[ ( fallbackIndex + i ) % VEHICLE_KEYS.length ];
		if ( models[ k ] ) return k;

	}

	return VEHICLE_KEYS[ 0 ];

}

function cloneVehicle( models, key ) {

	const source = models[ key ];
	if ( ! source ) return null;

	const clone = source.clone( true );
	clone.scale.setScalar( 0.5 );
	clone.traverse( ( child ) => {

		if ( child.isMesh ) {

			child.castShadow = true;
			child.receiveShadow = true;

		}

	} );

	return clone;

}

class RemoteKart {

	constructor( scene, group, name ) {

		this.scene = scene;
		this.group = group;
		this.name = name || '';
		this.buffer = [];
		this._tmpQ = new THREE.Quaternion();
		scene.add( group );

	}

	pushPose( serverT, pose, recvNow ) {

		this.buffer.push( { serverT, recvNow, pose } );
		if ( this.buffer.length > MAX_BUFFER ) this.buffer.shift();

	}

	step( now ) {

		if ( this.buffer.length === 0 ) return;

		if ( this.buffer.length === 1 ) {

			this._snap( this.buffer[ 0 ].pose );
			return;

		}

		// Render at now - INTERP_DELAY_MS in receive-time space.
		const renderTime = now - INTERP_DELAY_MS;

		let from = this.buffer[ 0 ];
		let to = this.buffer[ 1 ];
		for ( let i = this.buffer.length - 1; i > 0; i -- ) {

			if ( this.buffer[ i - 1 ].recvNow <= renderTime ) {

				from = this.buffer[ i - 1 ];
				to = this.buffer[ i ];
				break;

			}

		}

		const span = to.recvNow - from.recvNow;
		let alpha = span > 0 ? ( renderTime - from.recvNow ) / span : 1;
		if ( alpha < 0 ) alpha = 0;
		if ( alpha > 1 ) alpha = 1;

		const fp = from.pose.p;
		const tp = to.pose.p;
		this.group.position.set(
			fp[ 0 ] + ( tp[ 0 ] - fp[ 0 ] ) * alpha,
			fp[ 1 ] + ( tp[ 1 ] - fp[ 1 ] ) * alpha,
			fp[ 2 ] + ( tp[ 2 ] - fp[ 2 ] ) * alpha,
		);

		const fq = from.pose.q;
		const tq = to.pose.q;
		this.group.quaternion.set( fq[ 0 ], fq[ 1 ], fq[ 2 ], fq[ 3 ] );
		this._tmpQ.set( tq[ 0 ], tq[ 1 ], tq[ 2 ], tq[ 3 ] );
		this.group.quaternion.slerp( this._tmpQ, alpha );

	}

	_snap( pose ) {

		this.group.position.set( pose.p[ 0 ], pose.p[ 1 ], pose.p[ 2 ] );
		this.group.quaternion.set( pose.q[ 0 ], pose.q[ 1 ], pose.q[ 2 ], pose.q[ 3 ] );

	}

	dispose() {

		this.scene.remove( this.group );
		this.buffer.length = 0;
		this.group = null;

	}

}

export default class SocketManager {

	constructor( scene, models, options = {} ) {

		this.scene = scene;
		this.models = models;
		this.options = options;

		this.socket = null;
		this.connected = false;
		this.selfId = null;

		this.localVehicle = null;
		this.lapTimer = null;
		this._lastSeenLap = 0;

		this._sendAccum = 0;
		this._spawnIndex = 0;
		this.remotes = new Map();

		// Public state surfaced after connect resolves.
		this.self = null;          // { id, name, model, spawnIndex, bestLap }
		this.race = null;          // { state, startedAt }

		this.callbacks = {
			onWelcome: null,
			onJoined: null,
			onLeft: null,
			onLap: null,
			onState: null,
		};

	}

	setCallbacks( cbs ) {

		Object.assign( this.callbacks, cbs );

	}

	setLocalVehicle( vehicle ) {

		this.localVehicle = vehicle;

	}

	setLapTimer( lapTimer ) {

		this.lapTimer = lapTimer;
		this._lastSeenLap = lapTimer ? lapTimer.lastLap : 0;

	}

	async connect( serverUrl, options = {} ) {

		if ( ! serverUrl ) return;

		this.options = { ...this.options, ...options };

		const socket = io( serverUrl, {
			timeout: CONNECT_TIMEOUT_MS,
			reconnectionAttempts: RECONNECT_ATTEMPTS,
		} );
		this.socket = socket;

		socket.on( 'connect', () => {

			console.log( '[mp] connected', socket.id );
			socket.emit( 'player:join', {
				name: this.options.name,
				roomId: this.options.roomId || 'default',
				trackId: this.options.trackId || 'default',
			} );

		} );

		socket.on( 'disconnect', ( reason ) => {

			console.log( '[mp] disconnected', reason );
			this.connected = false;

		} );

		socket.on( 'player:joined', ( peer ) => {

			this._spawnRemote( peer );
			this.callbacks.onJoined?.( peer );

		} );

		socket.on( 'player:left', ( id ) => {

			const r = this.remotes.get( id );
			if ( r ) {

				r.dispose();
				this.remotes.delete( id );

			}
			this.callbacks.onLeft?.( id );

		} );

		socket.on( 'room:state', ( msg ) => {

			const recvNow = performance.now();
			for ( const player of msg.players ) {

				if ( player.id === this.selfId ) continue;
				const remote = this.remotes.get( player.id );
				if ( remote ) remote.pushPose( msg.t, player.pose, recvNow );

			}
			this.callbacks.onState?.( msg );

		} );

		socket.on( 'player:lap', ( msg ) => {

			this.callbacks.onLap?.( msg );

		} );

		await new Promise( ( resolve, reject ) => {

			const cleanup = () => {

				socket.off( 'room:welcome', onWelcome );
				socket.off( 'connect_error', onConnectError );

			};

			const onWelcome = ( msg ) => {

				cleanup();
				this.selfId = msg.self.id;
				this.self = msg.self;
				this.race = msg.race || null;
				this.track = msg.track || null;
				this.connected = true;
				for ( const peer of msg.peers ) this._spawnRemote( peer );
				this.callbacks.onWelcome?.( msg );
				resolve( msg );

			};

			const onConnectError = ( err ) => {

				cleanup();
				this.connected = false;
				this.socket = null;
				socket.close();
				reject( err );

			};

			socket.once( 'room:welcome', onWelcome );
			socket.once( 'connect_error', onConnectError );

		} );

	}

	_spawnRemote( peer ) {

		if ( ! peer || peer.id === this.selfId ) return;
		if ( this.remotes.has( peer.id ) ) return;

		const key = pickModelKey( this.models, peer.model, this._spawnIndex ++ );
		const group = cloneVehicle( this.models, key );
		if ( ! group ) return;

		this.remotes.set( peer.id, new RemoteKart( this.scene, group, peer.name ) );

	}

	update( dt ) {

		if ( this.connected && this.localVehicle && this.socket ) {

			this._sendAccum += dt;
			if ( this._sendAccum >= SEND_INTERVAL ) {

				this._sendAccum = 0;
				const v = this.localVehicle;
				const p = v.container.position;
				const q = v.container.quaternion;
				const mv = v.modelVelocity;
				this.socket.emit( 'player:state', {
					p: [ p.x, p.y, p.z ],
					q: [ q.x, q.y, q.z, q.w ],
					v: [ mv.x, mv.y, mv.z ],
					drift: v.driftIntensity || 0,
				} );

			}

		}

		if ( this.connected && this.lapTimer && this.socket ) {

			const last = this.lapTimer.lastLap;
			if ( last && last !== this._lastSeenLap ) {

				this._lastSeenLap = last;
				this.socket.emit( 'player:lap', { lapMs: Math.round( last * 1000 ) } );

			}

		}

		const now = performance.now();
		for ( const remote of this.remotes.values() ) remote.step( now );

	}

}
